/**
 * Detects chat intent to save N duplicate copies of the current HTML draft into the document project.
 * Returns null when the message is not a copies request.
 */
export function parseDraftDuplicateCopyRequest(text: string): number | null {
  const raw = text.trim();
  if (!raw) return null;
  const t = raw.toLowerCase();

  if (!/\b\d{1,2}\b/.test(t)) return null;
  if (!/\bcopies\b/.test(t) && !/\b\d{1,2}\s+copy\b/.test(t)) return null;

  const range = t.match(/\b(\d{1,2})\s*[-–to]+\s*(\d{1,2})\s+copies\b/);
  if (range) {
    const lo = parseInt(range[1], 10);
    const hi = parseInt(range[2], 10);
    const upper = Math.max(lo, hi);
    return Math.min(30, Math.max(1, upper));
  }

  const between = t.match(/\bbetween\s+(\d{1,2})\s+and\s+(\d{1,2})\s+copies\b/);
  if (between) {
    const lo = parseInt(between[1], 10);
    const hi = parseInt(between[2], 10);
    const upper = Math.max(lo, hi);
    return Math.min(30, Math.max(1, upper));
  }

  const n = t.match(/\b(\d{1,2})\s+copies\b/);
  if (n) return Math.min(30, Math.max(1, parseInt(n[1], 10)));

  const singular = t.match(/\b(\d{1,2})\s+copy\b/);
  if (singular) return Math.min(30, Math.max(1, parseInt(singular[1], 10)));

  return null;
}

/** True when the user wants the same edit applied across every HTML file in the project. */
export function parseBulkApplyAllDraftsIntent(text: string): boolean {
  const raw = text.trim();
  if (raw.length < 10) return false;
  if (parseDraftDuplicateCopyRequest(text) != null) return false;
  const t = raw.toLowerCase();
  return (
    /\ball\s+(?:the\s+)?(?:documents?|copies|versions?|files?)\b/.test(t)
    || /\b(?:in|on|across|to|for)\s+all\s+(?:documents?|copies|versions?|files?)\b/.test(t)
    || /\beach\s+(?:document|copy|version|file)\b/.test(t)
    || /\bin\s+every\s+(?:document|copy|version|file)\b/.test(t)
    || /\bapply\s+(?:this|that|it)\s+to\s+all\b/.test(t)
  );
}
